import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCodeContainer, verifyCredentialFromForm } from 'js/actions';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import { Base64 } from 'js-base64';
import {
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { getExistingReusableCustomProcessorId } from 'js/lib/sourceCustomProcessor/sourceCustomProcessorUtils';
import { initializeFormToNoSQLCategory } from 'new/components/flows/configure/NoSQL/NoSQL.utils';
import createFlowService from 'new/components/flows/service/createFlowService';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useDataMaps } from 'new/hooks/adapters/useDataMaps';
import useCredentials from 'new/state/credentials/repository/useCredentials';
import usePaging from 'new/state/paging/repository/usePaging';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import useFlowParams from 'new/components/flows/useFlowParams';

type UseParams = {
  id: string;
  flowType: string;
  resourceType: 'source' | 'sink';
};

export enum STATUS {
  INIT = 'INITIAL',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
}

const service = createFlowService();
const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;
export const useStartSource = () => {
  const [source, setSource] = useState<any>({});
  const [state, setState] = useState(STATUS.INIT);
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  const org = useCurrentOrg();
  const { vendors } = useVendors();
  const { fetchDataMaps } = useDataMaps();
  const { credentials } = useCredentials();
  const transforms = useSelector((s: any) => s.entities.transforms);
  const { id: sourceID } = useFlowParams();
  const { getMetaInfoById, getSourceConfigFields, getConnectionCategoryByCredID } = usePaging();
  const { connectionCategory, updateFormData, selectedCredential, selectedConnection } =
    useDirectFlow();

  /**
   * @Adapters
   */
  const { getSourceById } = useSourcesAdapter();
  const { requestFullVendor } = useVendorsAdapter();

  const flowService = createFlowService();

  const isEditFlow = Boolean(sourceID);
  const isCreateFlow = Boolean(isEditFlow === false);

  const verifyCredential = useCallback((credentialId: number) => {
    if (!credentialId) return;
    dispatch(verifyCredentialFromForm(FORM, credentialId))
      /**
       * - verify the credential
       * - get the data credential
       * - update form data
       */
      .then(() => setState(STATUS.IN_PROGRESS));
  }, []);

  useEffect(() => {
    if (isCreateFlow && connectionCategory === CONNECTION_CATEGORY_REST) {
      (async () => {
        fetchDataMaps();
        requestFullVendor(selectedConnection);
      })();
    }
  }, [connectionCategory]);

  useEffect(() => {
    (async () => {
      if (isEditFlow && state === STATUS.INIT) {
        try {
          const fetchedSource = await getSourceById(sourceID);
          const { data_credentials, data_credentials_id, code_container_id } = fetchedSource;
          const credID = data_credentials || data_credentials_id;
          const codeContainerIdToLoad =
            code_container_id || getExistingReusableCustomProcessorId(fetchedSource);

          const meta = getMetaInfoById(fetchedSource.source_type);
          const isRestAPI =
            meta?.connectionCategory === CONNECTION_CATEGORY_REST ||
            meta?.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API;

          if (isRestAPI) {
            fetchDataMaps();
            if (fetchedSource?.vendor_id) {
              const vendorName = vendors?.[fetchedSource.vendor_id]?.name;
              requestFullVendor(vendorName);
            }
          }

          if (codeContainerIdToLoad) {
            await dispatch(getCodeContainer(codeContainerIdToLoad));
          }

          verifyCredential(credID);

          setSource(fetchedSource);
        } catch (e) {
          console.error(`error to fetch the source with id ${sourceID}`);
        }
      }
    })();
  }, [state, sourceID, connectionCategory]);

  useEffect(() => {
    if (state === STATUS.IN_PROGRESS) {
      let newSourceConfig: any = {};
      let connectionCategory = selectedCredential
        ? getConnectionCategoryByCredID(selectedCredential?.id)
        : null;

      const connection = selectedConnection;
      const fields = connection ? getSourceConfigFields(connection) : [];

      let formData: any = {
        selectedConnection,
        sourceConfig: newSourceConfig,
        credentialId: selectedCredential?.id,
        selectedCredential: selectedCredential,
      };

      if (isEditFlow) {
        const { data_credentials, data_credentials_id, flow_triggers } = source;
        const credID = data_credentials || data_credentials_id;

        newSourceConfig = {
          flow_triggers,
          sourceName: source?.name,
          sourceDescription: source?.description,
          ...source['source_config'],
        };

        const connectionOrVendorType = getConnectionOrVendorType(
          source,
          vendors,
          'source_type',
          'vendor_id',
        );

        const connectionInfo = connectionOrVendorType && getMetaInfoById(connectionOrVendorType);
        connectionCategory = connectionInfo?.connectionCategory ?? null;

        formData = {
          source,
          credentialId: credID,
          sourceConfig: newSourceConfig,
          selectedCredential: credentials[credID],
          selectedConnection: connectionOrVendorType,
        };
      }

      switch (connectionCategory) {
        case CONNECTION_CATEGORY_FILE:
          let { config, selectedBucket } = flowService.initializeFormToFileCategory(
            source?.id
              ? source.source_config
              : service.createDefaultSourceConfig({
                  orgId: org.id,
                  configFields: fields,
                  sourceConfig: newSourceConfig,
                  selectedConnection: formData.selectedConnection,
                }),
            source,
            transforms,
          );

          formData = {
            ...formData,
            selectedBucket,
            sourceConfig: config,
          };
          break;

        case CONNECTION_CATEGORY_NOSQL:
          formData = {
            ...formData,
            ...initializeFormToNoSQLCategory({
              resource: source?.id ? source : {},
              config: source?.id
                ? source.source_config
                : service.createDefaultSourceConfig({
                    configFields: fields,
                    sourceConfig: newSourceConfig,
                    selectedConnection: formData.selectedConnection,
                    orgId: org.id,
                  }),
            }),
          };
          break;
        default:
          break;
      }

      setState(STATUS.FINISHED);
      updateFormData(formData);
    }
  }, [state]);
};
